// 0.78: Ajustes > Atualizações e sobre > Servidor. Só para as contas dos CPFs 950.454.575-00 e 064.815.751-25.
// A trava de verdade fica no servidor (função servidor_info): para as outras contas ele recusa.
// Mostra o espaço do banco e dos arquivos (Supabase, x limite do plano grátis), as contas e o relatório que o
// servidor da Locaweb manda a cada 10 minutos (disco, memória, processador, tempo ligado, site e cadeado).
import { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Text, View } from 'react-native';
import { C, alpha } from '../theme';
import { supabase } from '../../lib/supabase';
import { DEMO } from '../../lib/env';
import { t } from '../../lib/i18n';
import { brDateTime, brYmd } from '../../lib/date';
import { Recolhivel } from '../Recolhivel';
import { Card, Linha } from '../../screens/ajustes/comum';
import { BotaoAcao, Bloco } from './Linhas';

const DONOS = ['95045457500', '06481575125'];
export const ehDonoDoServidor = (cpf?: string | null) => DEMO || DONOS.includes(String(cpf ?? '').replace(/\D/g, ''));

// Limites do plano grátis do Supabase (se mudar de plano, trocar aqui).
const LIMITE_BANCO = 500 * 1024 ** 2;
const LIMITE_ARQUIVOS = 1024 ** 3;

type Vps = { disco_total?: number; disco_usado?: number; disco_livre?: number; mem_total?: number; mem_livre?: number; swap_total?: number; swap_livre?: number;
  cpus?: number; carga?: string; ligado_seg?: number; sistema?: string; site_bytes?: number; site_versoes?: number; site_atual?: string;
  servidor_web?: string; cadeado_ate?: string; pede_reinicio?: string };
type Info = { banco_bytes: number; dados_bytes: number; arquivos_bytes: number; arquivos_qtd: number; contas: number; contas_novas_7d: number; contas_ativas_7d: number;
  maiores: { nome: string; bytes: number }[]; vps: Vps | null; vps_em: string | null; agora: string };

const EXEMPLO: Info = { banco_bytes: 38e6, dados_bytes: 21e6, arquivos_bytes: 182e6, arquivos_qtd: 412, contas: 57, contas_novas_7d: 9, contas_ativas_7d: 31,
  maiores: [{ nome: 'planner_dados', bytes: 22e6 }, { nome: 'objects', bytes: 3e6 }, { nome: 'grupo_dados', bytes: 2e6 }],
  vps: { disco_total: 70e9, disco_usado: 18.4e9, disco_livre: 51.6e9, mem_total: 4e9, mem_livre: 2.6e9, swap_total: 0, swap_livre: 0, cpus: 2, carga: '0.12 0.20 0.18',
    ligado_seg: 23 * 86400, sistema: 'Ubuntu 24.04 LTS', site_bytes: 92e6, site_versoes: 5, site_atual: '20261024-101437', servidor_web: 'nginx', cadeado_ate: '2027-01-22', pede_reinicio: 'nao' },
  vps_em: new Date(Date.now() - 4 * 60000).toISOString(), agora: new Date().toISOString() };

export function bytes(n?: number | null) {
  const v = Number(n ?? 0);
  if (v >= 1024 ** 3) return `${(v / 1024 ** 3).toFixed(1).replace('.', ',')} GB`;
  if (v >= 1024 ** 2) return `${(v / 1024 ** 2).toFixed(1).replace('.', ',')} MB`;
  if (v >= 1024) return `${Math.round(v / 1024)} KB`;
  return `${v} B`;
}
const pct = (a: number, b: number) => (b > 0 ? Math.min(100, Math.round((a / b) * 100)) : 0);

/** Barra de uso: verde até 70%, amarela até 90%, vermelha acima. */
function Uso({ rotulo, usado, total, cor, extra }: { rotulo: string; usado: number; total: number; cor: string; extra?: string }) {
  const p = pct(usado, total);
  const c = p >= 90 ? C.danger : p >= 70 ? C.warn : cor;
  return (
    <View style={{ gap: 4, paddingVertical: 4 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 8 }}>
        <Text style={{ color: C.text, flexShrink: 1 }}>{rotulo}</Text>
        <Text style={{ color: p >= 90 ? C.danger : C.sub, fontSize: 13 }}>{t('{u} de {t} ({p}%)', { u: bytes(usado), t: bytes(total), p })}</Text>
      </View>
      <View style={{ height: 6, borderRadius: 3, backgroundColor: C.card2 }}>
        <View style={{ width: `${Math.max(2, p)}%`, height: 6, borderRadius: 3, backgroundColor: c }} />
      </View>
      {!!extra && <Text style={{ color: C.sub, fontSize: 12 }}>{extra}</Text>}
    </View>
  );
}
const Valor = ({ l, v, aviso }: { l: string; v: string; aviso?: boolean }) => (
  <Linha l={l}><Text style={{ color: aviso ? C.warn : C.sub, fontSize: 13, flexShrink: 1, textAlign: 'right' }} selectable>{v}</Text></Linha>
);
function tempo(seg: number) {
  const d = Math.floor(seg / 86400), h = Math.floor((seg % 86400) / 3600);
  return d ? t('{d} dia(s) e {h} h', { d, h }) : t('{h} h', { h });
}

export function QuadroServidor({ cor }: { cor: string }) {
  const [info, setInfo] = useState<Info | null>(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      if (DEMO || !supabase) { setInfo(EXEMPLO); return; }
      const { data, error } = await supabase.rpc('servidor_info');
      if (error) throw error;
      setInfo(data as Info);
    } catch (e: any) {
      const m = String(e?.message ?? e);
      setErro(/servidor_info|function|could not find/i.test(m) ? t('O servidor ainda não tem esta função. Rode o 53-SERVIDOR-0.78.bat no computador.')
        : /permiss/i.test(m) ? t('Esta conta não tem acesso a estas informações.') : m);
    } finally { setCarregando(false); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const v = info?.vps ?? null;
  const minutos = info?.vps_em ? Math.round((Date.parse(info.agora) - Date.parse(info.vps_em)) / 60000) : null;
  const atrasado = minutos == null || minutos > 30;
  const diasCadeado = v?.cadeado_ate ? Math.round((Date.parse(v.cadeado_ate + 'T12:00:00') - Date.now()) / 864e5) : null;

  return (
    <Bloco titulo={t('Servidor')}>
      <Card>
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('Só você vê este quadro.')}</Text>
        {carregando && !info && <ActivityIndicator color={cor} />}
        {!!erro && <Text style={{ color: C.warn }}>{erro}</Text>}
        {!!info && (<>
          <Text style={{ color: C.text, fontWeight: '800', marginTop: 4 }}>{t('Banco de dados (Supabase, plano grátis)')}</Text>
          <Uso rotulo={t('Banco')} usado={info.banco_bytes} total={LIMITE_BANCO} cor={cor} extra={t('Dados das abas: {d}', { d: bytes(info.dados_bytes) })} />
          <Uso rotulo={t('Arquivos')} usado={info.arquivos_bytes} total={LIMITE_ARQUIVOS} cor={cor} extra={t('{n} arquivo(s)', { n: info.arquivos_qtd })} />
          <Valor l={t('Contas')} v={t('{n} · {novas} nova(s) e {ativas} ativa(s) em 7 dias', { n: info.contas, novas: info.contas_novas_7d, ativas: info.contas_ativas_7d })} />
          {!!info.maiores?.length && (
            <Recolhivel titulo={t('Maiores tabelas')} chave="servidor-tabelas">
              {info.maiores.map(x => <Valor key={x.nome} l={x.nome} v={bytes(x.bytes)} />)}
            </Recolhivel>
          )}

          <View style={{ height: 1, backgroundColor: alpha(C.sub, 0.2), marginVertical: 6 }} />
          <Text style={{ color: C.text, fontWeight: '800' }}>{t('Servidor do site (Locaweb)')}</Text>
          {!v ? (
            <Text style={{ color: C.warn, fontSize: 13 }}>{t('O servidor ainda não mandou o relatório. Ele manda a cada 10 minutos depois do 53-SERVIDOR-0.78.bat.')}</Text>
          ) : (<>
            <Uso rotulo={t('Disco')} usado={v.disco_usado ?? 0} total={v.disco_total ?? 0} cor={cor} extra={t('Livre: {l} · site: {s}', { l: bytes(v.disco_livre), s: bytes(v.site_bytes) })} />
            <Uso rotulo={t('Memória em uso')} usado={(v.mem_total ?? 0) - (v.mem_livre ?? 0)} total={v.mem_total ?? 0} cor={cor} />
            <Valor l={t('Processador')} v={t('{n} núcleo(s) · carga {c}', { n: v.cpus ?? 0, c: v.carga ?? '-' })}
              aviso={!!v.cpus && Number(String(v.carga ?? '').split(' ')[1]) > v.cpus} />
            <Valor l={t('Ligado há')} v={tempo(v.ligado_seg ?? 0)} />
            <Valor l={t('Sistema')} v={`${v.sistema ?? '-'} · ${v.servidor_web ?? '-'}`} />
            <Valor l={t('Versão do site no ar')} v={t('{a} ({n} guardadas)', { a: v.site_atual ?? '-', n: v.site_versoes ?? 0 })} />
            <Valor l={t('Cadeado (HTTPS) até')} v={v.cadeado_ate ? brYmd(v.cadeado_ate) : t('sem cadeado')} aviso={diasCadeado == null || diasCadeado < 15} />
            {v.pede_reinicio === 'sim' && <Text style={{ color: C.warn, fontSize: 13 }}>{t('O sistema do servidor pede reinício (atualização de segurança instalada).')}</Text>}
          </>)}
          <Text style={{ color: atrasado ? C.warn : C.mute, fontSize: 11 }}>
            {info.vps_em ? t('Relatório do servidor: {q}', { q: brDateTime(info.vps_em) }) : ''}
            {info.vps_em && atrasado ? ' · ' + t('atrasado: confira se o servidor está ligado') : ''}
          </Text>
        </>)}
        <BotaoAcao cor={cor} label={carregando ? t('Atualizando...') : t('Atualizar')} disabled={carregando} onPress={carregar} />
      </Card>
    </Bloco>
  );
}
